import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {ExtractProps, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupProps} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useOnRequestClose} from '../../../../srcCommon/helpers/hookHelpers';
import {Dialog} from './dialog';
import {DialogButtonGroup} from './dialogButtonGroup';
import {DialogContent} from './dialogContent';
import {DialogHeader, DialogHeaderProps} from './dialogHeader';

/*
 * Props.
 */

interface ThreeButtonsDialogProps extends ExtractProps<typeof Dialog>, DialogHeaderProps, ButtonGroupProps {
  /** The text of the primary button. */
  primaryButtonLabel: ReactNode;
  /** Event handler for the primary button. */
  onPrimaryClick?: Handler;
  /** The text of the secondary button. */
  secondaryButtonLabel: ReactNode;
  /** Event handler for the secondary button. */
  onSecondaryClick?: Handler;
}

const defaultProps = {
  width: 460,
};

/*
 * Component.
 */

/** Dialog for when two actions are needed in addition to a cancel button. */
export const ThreeButtonsDialog: FC<ThreeButtonsDialogProps> = (props) => {
  // Register the "enter" key for the primary action.
  const {title, onPrimaryClick, onSecondaryClick} = props;
  const onRequestClose = useOnRequestClose(props.onRequestClose);

  const onPrimaryClickWithClose = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    }

    onRequestClose();
  };

  const onSecondaryClickWithClose = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }

    onRequestClose();
  };

  const keyHandlers = {
    [KeyboardActionsEnum.CONFIRM]: onPrimaryClickWithClose,
  };

  return (
    <Dialog {...props}>
      <KeyboardShortcuts handlers={keyHandlers}>
        <DialogHeader title={title} />
        <DialogContent>{props.children}</DialogContent>
        <DialogButtonGroup {...props}>
          <SameWidthButton onClick={props.onRequestClose}>
            <Trans id="dialog-cancel">Cancel</Trans>
          </SameWidthButton>
          <ButtonGroupSpacer />
          <SameWidthButton onClick={onSecondaryClickWithClose}>{props.secondaryButtonLabel}</SameWidthButton>
          <SameWidthButton variant={ButtonVariantsEnum.PRIMARY} onClick={onPrimaryClickWithClose}>
            {props.primaryButtonLabel}
          </SameWidthButton>
        </DialogButtonGroup>
      </KeyboardShortcuts>
    </Dialog>
  );
};
ThreeButtonsDialog.defaultProps = defaultProps;
